// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useState, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { MOCK_SOPS, MOCK_SOP_VERSION_DIFFS } from '../mockData';
import type { SOP, SOPVersion, SOPVersionDiff } from '../../../types';
import './SOPVersionDetail.css';

const MOCK_EXECUTIONS = [
  { date: 'Feb 28', name: 'Peak Response #47', status: 'Completed', planId: 'rec-peak-47' },
  { date: 'Feb 14', name: 'Peak Response #43', status: 'Completed', planId: 'rec-peak-43' },
  { date: 'Jan 30', name: 'Peak Response #41', status: 'Completed', planId: 'rec-peak-41' },
];

function SOPVersionDetail() {
  const { site_id, sopId, versionId } = useParams();
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') === 'diff' ? 'diff' : 'full';
  const [viewMode, setViewMode] = useState<'full' | 'diff'>(initialMode);

  const sop = useMemo<SOP | undefined>(() => MOCK_SOPS.find(s => s.id === sopId), [sopId]);

  const version = useMemo<SOPVersion | undefined>(
    () => (sop ? sop.versions.find(v => v.id === versionId) : undefined),
    [sop, versionId]
  );

  const diff = useMemo<SOPVersionDiff | undefined>(
    () => MOCK_SOP_VERSION_DIFFS.find(d => d.versionId === versionId),
    [versionId]
  );

  if (!sop || !version) {
    return (
      <div className="sop-version-detail-container">
        <Link to={`/site/${site_id}/sop/${sopId}/versions`} className="sop-back-link">
          &larr; Version History
        </Link>
        <p>Version not found.</p>
      </div>
    );
  }

  return (
    <div className="sop-version-detail-container">
      <Link to={`/site/${site_id}/sop/${sopId}/versions`} className="sop-back-link">
        &larr; Version History
      </Link>
      <p className="sop-version-subtitle">{sop.name}</p>

      {/* Version Header Card */}
      <div className="version-header-card">
        <div className="version-header-top">
          <span className="version-header-number">{version.versionNumber}</span>
          <span
            className={`version-header-status version-header-status-${version.status === 'Active' ? 'active' : 'superseded'}`}
          >
            {version.status}
          </span>
        </div>
        <p className="version-header-date">
          {version.date} &middot; {version.author}
        </p>
        <p className="version-header-reason">
          <em>&ldquo;{version.changeReason}&rdquo;</em>
        </p>
        <p className="version-header-approver">
          Approved: {version.approvalDate} by {version.approver}
        </p>
      </div>

      {/* View Mode Toggle */}
      <div className="version-view-toggle">
        <button
          className={`view-toggle-btn ${viewMode === 'full' ? 'view-toggle-active' : ''}`}
          onClick={() => setViewMode('full')}
        >
          Full Document
        </button>
        <button
          className={`view-toggle-btn ${viewMode === 'diff' ? 'view-toggle-active' : ''}`}
          onClick={() => setViewMode('diff')}
        >
          Changes from {diff ? diff.previousVersion : 'previous'}
        </button>
      </div>

      {/* Conditional Content */}
      {viewMode === 'full' ? (
        <div className="version-full-content">
          {/* Overview */}
          <div className="version-section">
            <h3 className="version-section-title">Overview</h3>
            <p className="version-section-text">{sop.overview}</p>
          </div>

          {/* Safety Boundaries */}
          <div className="version-section">
            <h3 className="version-section-title">Safety Boundaries</h3>
            <table className="version-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Min</th>
                  <th>Max</th>
                  <th>Unit</th>
                </tr>
              </thead>
              <tbody>
                {sop.safetyBoundaries.map((b, i) => (
                  <tr key={i}>
                    <td>{b.parameter}</td>
                    <td>{b.min}</td>
                    <td>{b.max}</td>
                    <td>{b.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Equipment Configuration */}
          <div className="version-section">
            <h3 className="version-section-title">Equipment Configuration</h3>
            <table className="version-table">
              <thead>
                <tr>
                  <th>Equipment</th>
                  <th>Parameter</th>
                  <th>Normal</th>
                  <th>Optimized</th>
                </tr>
              </thead>
              <tbody>
                {sop.equipmentConfig.map((eq, i) => (
                  <tr key={i}>
                    <td>{eq.equipment}</td>
                    <td>{eq.parameter}</td>
                    <td>{eq.normal}</td>
                    <td>{eq.optimized}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pre-Execution Checks */}
          <div className="version-section">
            <h3 className="version-section-title">Pre-Execution Checks</h3>
            <ul className="version-checklist">
              {sop.preChecks.map((check, i) => (
                <li key={i}>{check}</li>
              ))}
            </ul>
          </div>

          {/* Post-Execution Checks */}
          <div className="version-section">
            <h3 className="version-section-title">Post-Execution Checks</h3>
            <ul className="version-checklist">
              {sop.postChecks.map((check, i) => (
                <li key={i}>{check}</li>
              ))}
            </ul>
          </div>

          {/* Rollback Instructions */}
          <div className="version-section">
            <h3 className="version-section-title">Rollback Instructions</h3>
            <ol className="version-checklist">
              {sop.rollbackInstructions.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </div>
        </div>
      ) : (
        <div className="version-diff-content">
          {diff && diff.sections.length > 0 ? (
            <>
              {diff.sections.map((section, si) => (
                <div key={si} className="diff-section">
                  <h3 className="diff-section-title">{section.title}</h3>
                  <div className="diff-changes">
                    {section.changes.map((change, ci) => (
                      <div key={ci} className="diff-change-item">
                        {change.type === 'modified' && (
                          <>
                            <div className="diff-line diff-line-removed">
                              - {change.label}: {change.removed}
                            </div>
                            <div className="diff-line diff-line-added">
                              + {change.label}: {change.added}
                            </div>
                          </>
                        )}
                        {change.type === 'added' && (
                          <div className="diff-line diff-line-added">+ {change.label} (added)</div>
                        )}
                        {change.type === 'removed' && (
                          <div className="diff-line diff-line-removed">
                            - {change.label} (removed)
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <p className="diff-no-other-changes">No other sections changed.</p>
            </>
          ) : (
            <p className="diff-not-available">No diff data available for this version.</p>
          )}
        </div>
      )}

      {/* Executions Using This Version */}
      <div className="version-executions-section">
        <h3 className="version-section-title">Executions Using This Version</h3>
        <div className="version-executions-list">
          {MOCK_EXECUTIONS.map(exec => (
            <div key={exec.planId} className="version-execution-item">
              <div className="execution-item-info">
                <span className="execution-item-date">{exec.date}</span>
                <span className="execution-item-name">{exec.name}</span>
                <span className="execution-item-status execution-status-completed">
                  {exec.status}
                </span>
              </div>
              {/* exec.planId is a mock placeholder string, not a real HITL
                  approvalId, so link to the list rather than a detail page
                  that can't resolve it. */}
              <Link to="/optimizations" className="execution-view-link">
                View
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default SOPVersionDetail;
